import { getInvitationDetailsApi } from '@/api-calls/staff/get'
import { acceptInvitationApi } from '@/api-calls/staff/mutate'
import { useAuth } from '@/auth/auth-context'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@blogging/ui'
import { useFetch, useMutation } from '@ngtrvu/use-api'
import {
  AlertCircle,
  Building2,
  Calendar,
  Check,
  Globe,
  Loader2,
  Mail,
  Shield,
  User,
  UserPlus,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

const PENDING_INVITATION_KEY = 'pending_invitation_token'

interface InvitationDetails {
  email: string
  role: string
  site: {
    name: string | null
    slug: string
  }
  inviter: {
    name: string | null
    email: string
  }
  expiresAt: string
  isExpired: boolean
}

export function InvitationAcceptPage() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [isAccepting, setIsAccepting] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [declined, setDeclined] = useState(false)
  const [needsAuth, setNeedsAuth] = useState(false)

  // Fetch invitation details (public endpoint, no auth required)
  const {
    data: invitationDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
  } = useFetch(getInvitationDetailsApi, {
    resourceName: 'invitationDetails',
    initialParams: { token: token as string },
  })

  const { mutate: acceptInvitation, isPending } = useMutation(
    acceptInvitationApi,
    {
      resourceName: 'invitation',
      onSuccess: (data: any) => {
        setAccepted(true)
        setIsAccepting(false)
        toast.success('Invitation accepted!')

        // Clear pending invitation from storage
        localStorage.removeItem(PENDING_INVITATION_KEY)

        // Redirect to site dashboard after 2 seconds
        setTimeout(() => {
          if (data?.site?.id) {
            navigate(`/${data.site.slug || data.site.id}`)
          } else {
            navigate('/')
          }
        }, 2000)
      },
      onError: (error: any) => {
        setIsAccepting(false)
        toast.error(error?.message || 'Failed to accept invitation')
      },
    },
  )

  // Check if user is authenticated and handle unauthenticated users
  useEffect(() => {
    if (authLoading) return // Wait for auth check to complete

    console.log('[Invitation Page] Auth status:', {
      isAuthenticated,
      token,
      needsAuth,
    })

    if (!isAuthenticated && token) {
      // User is not authenticated, store the invitation token and show auth required
      console.log(
        '[Invitation Page] Storing token and showing auth required:',
        token,
      )
      localStorage.setItem(PENDING_INVITATION_KEY, token)
      setNeedsAuth(true)
    } else if (isAuthenticated && needsAuth) {
      // User just authenticated, reset needsAuth flag
      console.log(
        '[Invitation Page] User is now authenticated, resetting needsAuth',
      )
      setNeedsAuth(false)
    }
  }, [isAuthenticated, authLoading, token, needsAuth])

  const handleAccept = () => {
    if (!token) return
    setIsAccepting(true)
    acceptInvitation({ token })
  }

  const handleDecline = () => {
    setDeclined(true)
    toast.info('Invitation declined')
    setTimeout(() => {
      navigate('/')
    }, 1500)
  }

  const roleColors = {
    admin: 'destructive',
    editor: 'default',
    author: 'secondary',
  } as const

  const roleDescriptions = {
    admin: 'Full access to manage the site, content, and team',
    editor: 'Manage all posts, pages, and tags',
    author: 'Create and manage your own posts',
  }

  // Component to show invitation details
  const InvitationPreview = ({ details }: { details: InvitationDetails }) => {
    const expiryDate = new Date(details.expiresAt)
    const isExpiringSoon =
      !details.isExpired &&
      expiryDate.getTime() - Date.now() < 24 * 60 * 60 * 1000 // Less than 24 hours

    return (
      <div className="space-y-4">
        {/* Site Information - Hero Section */}
        <div className="rounded-lg border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-6">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                You're invited to join
              </p>
              <h3 className="text-2xl font-bold text-foreground truncate mb-1">
                {details.site.name || details.site.slug}
              </h3>
              {details.site.name && details.site.slug && (
                <div className="flex items-center gap-1.5">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground truncate">
                    {details.site.slug}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Other Details */}
        <div className="rounded-lg border bg-card p-5 space-y-4">
          {/* Role Information - Enhanced */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Your role
              </p>
              <Badge
                variant={roleColors[details.role as keyof typeof roleColors]}
                className="mb-2"
              >
                {details.role.charAt(0).toUpperCase() + details.role.slice(1)}
              </Badge>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {
                  roleDescriptions[
                    details.role as keyof typeof roleDescriptions
                  ]
                }
              </p>
            </div>
          </div>

          {/* Inviter Information - Enhanced */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-4 w-4 text-primary" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Invited by
              </p>
              <p className="text-sm font-semibold">
                {details.inviter.name || 'Team member'}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {details.inviter.email}
              </p>
            </div>
          </div>

          {/* Email Information */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Invitation sent to
              </p>
              <p className="text-sm font-medium truncate">{details.email}</p>
            </div>
          </div>

          {/* Expiration Information */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Expires
              </p>
              <p className="text-sm font-medium">
                {expiryDate.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Status Alerts */}
        {details.isExpired && (
          <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-destructive mb-1">
                  This invitation has expired
                </p>
                <p className="text-xs text-destructive/80">
                  Please contact {details.inviter.name || 'the team'} for a new
                  invitation.
                </p>
              </div>
            </div>
          </div>
        )}

        {isExpiringSoon && (
          <div className="rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-1">
                  Expires soon
                </p>
                <p className="text-xs text-orange-800 dark:text-orange-200">
                  This invitation will expire in less than 24 hours.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Show error screen for invalid token or not found invitation
  if (!token || (detailsError && !authLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-destructive/10 mx-auto mb-4">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {!token ? 'Invalid Invitation Link' : 'Invitation Not Found'}
            </CardTitle>
            <CardDescription>
              {!token
                ? 'This invitation link is not valid. Please check your email for the correct link.'
                : 'This invitation may have expired, been cancelled, or the link is incorrect.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Show site info if we have it */}
            {invitationDetails && (
              <div className="rounded-lg border bg-muted/30 p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">
                      Invitation was for
                    </p>
                    <p className="font-semibold truncate">
                      {invitationDetails.site.name ||
                        invitationDetails.site.slug}
                    </p>
                    {invitationDetails.site.name &&
                      invitationDetails.site.slug && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Globe className="h-3 w-3 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground truncate">
                            {invitationDetails.site.slug}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg bg-muted/50 border p-4">
              <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                What can you do?
              </p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-medium">•</span>
                  <span>Check your email for the correct invitation link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-medium">•</span>
                  <span>
                    Ask the person who invited you to send a new invitation
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-medium">•</span>
                  <span>Contact support if you need assistance</span>
                </li>
              </ul>
            </div>
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show authentication required screen
  // Only show if we're sure user is not authenticated (not loading) AND not authenticated
  if (!authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-xl">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mx-auto mb-4">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">
              You've been invited!
            </CardTitle>
            <CardDescription>
              Sign in or create an account to accept this invitation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Show invitation details if loaded */}
            {isLoadingDetails ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                <p className="text-sm text-muted-foreground">
                  Loading invitation details...
                </p>
              </div>
            ) : invitationDetails ? (
              <>
                <InvitationPreview details={invitationDetails} />

                {!invitationDetails.isExpired && (
                  <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 p-4">
                    <div className="flex items-start gap-3">
                      <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-900 dark:text-blue-100 leading-relaxed">
                        After signing in, you'll be able to accept this
                        invitation and start collaborating on{' '}
                        <span className="font-semibold">
                          {invitationDetails.site.name ||
                            invitationDetails.site.slug}
                        </span>
                        !
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : null}

            <div className="space-y-2">
              <Button asChild className="w-full">
                <Link to="/login">Continue to sign in</Link>
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Go back to the{' '}
                <button
                  onClick={() => {
                    localStorage.removeItem(PENDING_INVITATION_KEY)
                    navigate('/')
                  }}
                  className="underline hover:text-foreground"
                >
                  dashboard
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 mx-auto mb-4 animate-in zoom-in duration-300">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Welcome to the Team!
            </CardTitle>
            <CardDescription>
              You've successfully accepted the invitation. Redirecting you to
              your new site...
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (declined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted mx-auto mb-4">
              <X className="h-7 w-7 text-muted-foreground" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Invitation Declined
            </CardTitle>
            <CardDescription>
              You chose not to accept this invitation. Redirecting you to the
              dashboard...
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show loading while checking auth status
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-muted-foreground">
              Loading invitation...
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-xl">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-50 dark:bg-green-950 mx-auto mb-4">
            <UserPlus className="h-7 w-7 text-green-600 dark:text-green-500" />
          </div>
          <CardTitle className="text-2xl font-bold">
            You've Been Invited!
          </CardTitle>
          <CardDescription>
            Review the invitation details and accept to join the team
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Show invitation details */}
          {isLoadingDetails ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground">
                Loading invitation details...
              </p>
            </div>
          ) : invitationDetails ? (
            <>
              <InvitationPreview details={invitationDetails} />

              {!invitationDetails.isExpired && (
                <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20 p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-green-900 dark:text-green-100 font-semibold mb-1">
                        Ready to join?
                      </p>
                      <p className="text-sm text-green-800 dark:text-green-200 leading-relaxed">
                        By accepting, you'll gain{' '}
                        <span className="font-semibold">
                          {invitationDetails.role}
                        </span>{' '}
                        access to this site and be able to collaborate according
                        to your role.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : null}

          <div className="flex gap-3">
            <Button
              onClick={handleDecline}
              variant="outline"
              className="flex-1"
              disabled={
                isAccepting || isPending || invitationDetails?.isExpired
              }
            >
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              className="flex-1"
              disabled={
                isAccepting || isPending || invitationDetails?.isExpired
              }
            >
              {(isAccepting || isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {invitationDetails?.isExpired ? 'Expired' : 'Accept Invitation'}
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Not expecting this invitation?{' '}
            <button
              onClick={() => navigate('/')}
              className="underline hover:text-foreground"
            >
              Go to Dashboard
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
